/* DocumentCounterCrudCard.tsx
    Une card affichant la gestion des compteurs de documents
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import {
	DocumentCountersData,
	documentTypeNames,
	documentCountersApi,
} from 'features/documentCounters';
import { faStopwatch20 } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface DocumentCounterCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Force l'affichage de faStopwatch20 même en taille lg/md. Utile quand la card est intégrée dans une page sans WidgetHeader propre. Défaut : false */
	forceIcon?: boolean;
}

// DocumentCounterCrudCard component
export const DocumentCounterCrudCard: React.FC<
	DocumentCounterCrudCardProps
> = ({
	size = 'sm',
	returnPath = '/app/document-counters',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	forceIcon = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [
					{
						label: 'ID',
						field: 'id',
						render: (a: DocumentCountersData) => a.id,
					},
				]
			: []),
		//Colonne "Affaire"
		...(extended
			? [
					{
						label: 'Type',
						field: 'none',
						render: (a: DocumentCountersData) =>
							a.documentType ? documentTypeNames[a.documentType] : '',
					},
				]
			: []),
		// Colonne "Année"
		{
			label: 'Année',
			field: 'year',
			render: (a: DocumentCountersData) => a.year,
		},
		// Colonne "Dernier numéro"
		{
			label: 'Dernier numéro',
			field: 'lastNumber',
			render: (a: DocumentCountersData) => a.lastNumber,
		},
		// Colonne "Date de début" au format jour/mois/année uniquement si extended
		...(extended
			? [
					{
						label: 'Date',
						field: 'updatedAt',
						filterType: 'date' as const,
						render: (a: DocumentCountersData) =>
							new Date(a.updatedAt || '').toLocaleDateString('fr-FR'),
					},
				]
			: []),
	];

	return (
		<GenericCrudCard<DocumentCountersData>
			queryKey="documentCounters"
			title={
				size === 'sm'
					? 'Compteurs chrono'
					: 'Liste des compteurs chronologiques'
			}
			fetchData={(token, options) =>
				documentCountersApi.listPaginated(token, options)
			}
			tableColumns={columns}
			getEditPath={(id) => `/app/documentCounters/${id}/edit`}
			emptyMessage="Aucun compteur trouvé."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' || forceIcon ? faStopwatch20 : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default DocumentCounterCrudCard;
